import { FilterTypes } from '@aboutyou/backbone/endpoints/filters/filters'
import { useMedia } from 'helpers/hooks/useMedia'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { useObserver } from 'mobx-react-lite'
import React, { FC, useMemo, useState } from 'react'
import {
  FilterGroupItem,
  isFilterGroup,
} from 'services/providers/bapi/entities/filters/types'
import ErrorBoundary from 'sharedComponents/Utils/ErrorBoundary'
import { useFilterStore } from 'store/sidecar/ProductStreamContext'
import styled, { css } from 'styled-components'
import ExpandButton from 'uiComponents/atoms/Buttons/ExpandButton'
import ResetButton from 'uiComponents/atoms/Buttons/ResetButton'

import { filterOutByPredefinedSlug } from 'sharedComponents/ProductFilter/util/filterOutByPredefinedSlug'
import AttributeFilter from './Filters/AttributesFilter'
import BooleanFilter from './Filters/BooleanFilters/BooleanFilter'
import GroupFilter from './Filters/GroupFilter'
import RangeFilter from './Filters/RangeFilter'
import { reorderSaleAndSustainableFilterGroups } from './util/reorderSaleAndSustainableFilterGroups'

const FilterContainer = styled.div<{
  alignHorizontally: boolean
  increasedTopSpacing?: boolean
}>`
  width: ${({ alignHorizontally }) => (alignHorizontally ? '100%' : '200px')};
  margin-bottom: 30px;
  margin-top: ${({ increasedTopSpacing }) =>
    increasedTopSpacing ? '4px' : '0'};

  @media only screen and (min-width: 1024px) {
    margin-top: ${({ increasedTopSpacing }) =>
      increasedTopSpacing ? '8px' : '0'};
  }
`

const FilterSection = styled.section<{ alignHorizontally: boolean }>`
  display: flex;
  align-items: center;

  margin-bottom: ${props => (props.alignHorizontally ? 0 : '14px')};

  &:empty {
    display: none;
  }

  ${({ alignHorizontally }) =>
    alignHorizontally &&
    css`
      margin: 0 12px 12px 0;

      &:last-child {
        margin-right: 0;
      }
    `}
`

const FilterWrapper = styled.div<{ alignHorizontally: boolean }>`
  ${({ alignHorizontally }) =>
    alignHorizontally &&
    css`
      display: flex;
      flex-wrap: wrap;
    `}
`

const ActionButtons = styled.div`
  height: 38px;
  display: flex;
  align-items: center;
  flex-wrap: no-wrap;
  margin-left: 10px;
`

const StyledExpandButton = styled(ExpandButton)`
  margin-right: 20px;
`

const StyledResetButton = styled(ResetButton)`
  margin-left: 0;
`

const mapFilterGroups = (filterGroup: FilterGroupItem) => {
  const key = isFilterGroup(filterGroup)
    ? filterGroup.filterGroupSlug
    : filterGroup.slug

  return (
    <ErrorBoundary key={key}>
      <FilterSection alignHorizontally>
        <Filter filter={filterGroup} />
      </FilterSection>
    </ErrorBoundary>
  )
}

const FilterBar: FC = () => {
  const filterStore = useFilterStore()
  const { t } = useTranslation()

  const defaultNumOfFilters = useMedia(
    {
      'screen and (max-width: 900px)': 8,
      'screen and (max-width: 1280px)': 6,
      'screen and (max-width: 1440px)': 8,
    },
    11,
  )

  const [expanded, setExpanded] = useState(false)

  const availableFilterGroups = useObserver(
    () => filterStore.availableFilterGroups,
  )

  const maxItemsToShow = !expanded ? defaultNumOfFilters : undefined

  const filterGroups = useMemo(
    () =>
      reorderSaleAndSustainableFilterGroups(
        availableFilterGroups.filter(filterGroup =>
          filterOutByPredefinedSlug(filterGroup),
        ),
      ),
    [availableFilterGroups],
  )

  const showExpandButton = filterGroups.length > defaultNumOfFilters

  return (
    <FilterContainer
      data-testid={`FilterBar_Horizontal`}
      alignHorizontally
      increasedTopSpacing
    >
      <FilterWrapper alignHorizontally>
        {filterGroups
          .slice(0, maxItemsToShow)
          .map(filterGroup => mapFilterGroups(filterGroup))}
        <ActionButtons>
          {showExpandButton && (
            <StyledExpandButton
              collapseLabel={t('Weniger Filter')}
              expanded={expanded}
              expandLabel={t('Mehr Filter')}
              onClick={() => setExpanded(!expanded)}
            />
          )}
          {filterStore.selectedFiltersCount > 1 && (
            <StyledResetButton
              data-testid="ClearButton"
              disabled={filterStore.selectedFiltersCount === 0}
              label={t('Alle zurücksetzen')}
              onClick={filterStore.clearFilter}
            />
          )}
        </ActionButtons>
      </FilterWrapper>
    </FilterContainer>
  )
}

export default FilterBar

const Filter: FC<{ filter: FilterGroupItem }> = ({ filter }) => {
  switch (filter.type) {
    case '__FILTER_GROUP__':
      return <GroupFilter filterGroup={filter} />
    case FilterTypes.BOOLEAN:
      return <BooleanFilter filter={filter} />
    case FilterTypes.ATTRIBUTES:
      return <AttributeFilter {...filter} />
    case FilterTypes.RANGE:
      return <RangeFilter filter={filter} />
    default:
      return null
  }
}
